import ApeironComponent from '@/components/commons/apeiron-component.model';
import AlertService from '@/shared/alert/alert.service';
import { TipoAccion } from '@/shared/model/enumerations/tipo-accion.model';
import CatalogosSnp from '@/shared/model/snp/catalogos-snp.factory';
import { ICuota, MenuCuota } from '@/shared/model/snp/cuota.model';
import { Cuota, Solicitud } from '@/shared/model/solicitud.model';
import { Component, Inject, Prop } from 'vue-property-decorator';
import { minValue, required, requiredIf } from 'vuelidate/lib/validators';

const validations = function () {
  const { cuota } = this as CuotaComponent;
  return {
    cuota: {
      cobra: { required },
      esVoluntario: { required: requiredIf(() => cuota.cobra === true) },
      frecuenciaColegiatura: {
        required: value => {
          if (cuota.cobra) {
            if (cuota.montoColegiatura === 0 || cuota.montoColegiatura > 0) {
              return !!value;
            }
            if (!cuota.frecuenciaInscripcion && !cuota.frecuenciaOtraCuota) {
              return !!value;
            }
          }
          return true;
        },
      },

      frecuenciaInscripcion: {
        required: value => {
          if (cuota.cobra) {
            if (cuota.montoInscripcion === 0 || cuota.montoInscripcion > 0) {
              return !!value;
            }
            if (!cuota.frecuenciaColegiatura && !cuota.frecuenciaOtraCuota) {
              return !!value;
            }
          }
          return true;
        },
      },
      frecuenciaOtraCuota: {
        required: value => {
          if (cuota.cobra) {
            if (cuota.montoOtra === 0 || cuota.montoOtra > 0) {
              return !!value;
            }
            if (!cuota.frecuenciaColegiatura && !cuota.frecuenciaInscripcion) {
              return !!value;
            }
          }
          return true;
        },
      },
      montoColegiatura: {
        required: requiredIf(() => cuota.frecuenciaColegiatura !== undefined && cuota.frecuenciaColegiatura !== null),
        minValue: minValue(1),
      },
      montoInscripcion: {
        required: requiredIf(() => cuota.frecuenciaInscripcion !== undefined && cuota.frecuenciaInscripcion !== null),
        minValue: minValue(1),
      },
      montoOtra: {
        required: requiredIf(() => cuota.frecuenciaOtraCuota !== undefined && cuota.frecuenciaOtraCuota !== null),
        minValue: minValue(1),
      },
      tipoCuota: {
        required: requiredIf(() => cuota.frecuenciaOtraCuota !== undefined && cuota.frecuenciaOtraCuota !== null),
      },
    },
  };
};

@Component({ validations })
export default class CuotaComponent extends ApeironComponent {
  @Prop({ required: true })
  public solicitud: Solicitud;

  public cuota: Cuota = {};

  public frecuenciaColegiatura: ICuota = {};
  public frecuenciaInscripcion: ICuota = {};
  public frecuenciaOtraCuota: ICuota = {};
  public frecuenciasCuota: Array<MenuCuota> = [];
  public formularioRequerido = false;

  mounted(): void {
    this.get();
  }
  /**
   * Carga inicial del componente
   *
   * @memberof CuotaComponent
   */
  public get(): void {
    if (this.solicitud?.cuota) {
      this.cuota = Cuota.fromObject(this.solicitud.cuota);
    } else {
      this.formularioRequerido = true;
    }

    this.frecuenciasCuota = [];
    CatalogosSnp.obtenerFrecuenciasCuota().forEach(opcion => this.frecuenciasCuota.push(MenuCuota.fromObject(opcion)));
  }

  public manejarCambiaFrecuenciaColegiatura(): void {
    this.cuota.montoColegiatura = undefined;
  }

  public manejarCambiaFrecuenciaInscripcion(): void {
    this.cuota.montoInscripcion = undefined;
  }

  public manejarCambiaFrecuenciaOtras(): void {
    this.cuota.montoOtra = undefined;
    this.cuota.tipoCuota = undefined;
  }

  public reiniciarDatos(): void {
    const valorCuota = this.cuota.cobra;
    this.cuota = new Cuota();
    this.cuota.cobra = valorCuota;
  }

  get desactivarBoton(): boolean {
    return this.$v.$invalid;
  }

  public options = [
    { text: 'Sí', value: true },
    { text: 'No', value: false },
  ];

  /**
   * Guarda la cuota con los valores que le ha dado el usuario.
   *
   * @memberof CuotaComponent
   */
  public guardarCuota(): void {
    let datosEnviar: { cuota: Cuota };

    if (this.cuota.cobra === false) {
      datosEnviar = { cuota: { cobra: false } };
    } else {
      const cuotaFiltrada: Cuota = { ...this.cuota };

      // Verificar si los montos son mayores a 0 y no están vacíos
      if (cuotaFiltrada.montoColegiatura <= 0) {
        delete cuotaFiltrada.montoColegiatura;
      }

      if (cuotaFiltrada.montoInscripcion <= 0) {
        delete cuotaFiltrada.montoInscripcion;
      }

      if (cuotaFiltrada.montoOtra <= 0) {
        delete cuotaFiltrada.montoOtra;
      }

      datosEnviar = { cuota: cuotaFiltrada };
    }

    this.$emit('guardarCuota', datosEnviar);
  }

  /**
   * Determina si un campo debe mostrarse.
   *
   * @memberof CuotaComponent
   */
  get mostrarCampo(): boolean {
    return this.cuota.cobra === true;
  }

  /**
   * Determina si un campo tipo de cuota debe mostrarse.
   *
   * @memberof CuotaComponent
   */
  get mostrarTipoCuota() {
    if (this.cuota.frecuenciaOtraCuota && this.cuota.cobra === true) {
      return true;
    }
    return false;
  }

  /**
   * Indicador para permitir el modo escritura
   */
  get esEscritura(): boolean {
    return this.esEscrituraConEstadoEnCaptura(this.solicitud.estado);
  }
}
